/**
 * CostsStep.tsx
 *
 * Build Bar — Step 4: Costos.
 *
 * Full layout (NO page scroll — each panel scrolls internally):
 *
 *   ┌─ .container (flex column, height: 100%) ──────────────────────────────────┐
 *   │  ┌─ .toolbar (flex-shrink: 0) ────────────────────────────────────────── │
 *   │  │  Provider chips  ·  + Add table  ·  Grand total  ·  Avg. margin  │ │
 *   │  └─────────────────────────────────────────────────────────────────────── │
 *   │  ┌─ .editorArea (flex row, flex: 1, min-height: 0) ──────────────────── │
 *   │  │  ┌─ .sidebar (overflow-y: auto) ─────────────────────────────────── │ │
 *   │  │  │  Table miniature cards  ·  "+ Add table"                    │ │ │
 *   │  │  └─────────────────────────────────────────────────────────────────── │ │
 *   │  │  ┌─ .mainArea (flex: 1, overflow-y: auto) ─────────────────────── │ │ │
 *   │  │  │  CostTableEditor  ·  Provider prices (collapsible)  ·  Assist │ │ │
 *   │  │  └─────────────────────────────────────────────────────────────────── │ │
 *   │  └─────────────────────────────────────────────────────────────────────── │
 *   │  ┌─ .footerRow (flex-shrink: 0) ─────────────────────────────────────── │
 *   │  └─────────────────────────────────────────────────────────────────────── │
 *   └───────────────────────────────────────────────────────────────────────────┘
 *
 * Auto-save is debounced 500 ms via useSaveCosts whenever cost tables change.
 * The "Continue to generate" button advances to Build step 5.
 */

import { useEffect, useCallback, useRef, useState } from 'react';

import toast from 'react-hot-toast';

import { Skeleton, Button } from '@/components/ui';
import { extractApiError } from '@/lib/axios';
import { useWizardStore } from '@/stores/useWizardStore';
import { useCostData, useSaveCosts, useCostAssist } from '@/api/useWizardV2';
import type { CostTableConfig } from '@/types/wizardV2';
import { AiAssistInput } from '../components/AiAssistInput';
import { CostTableEditor } from './CostTableEditor';
import styles from './CostsStep.module.css';

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * CostsStep — Build bar step 4.
 *
 * Reads cost tables from the store and from the backend (useCostData).
 * Writes back through useSaveCosts with 500 ms debounce.
 * AI assistance is available via useCostAssist.
 */
export const CostsStep = () => {
  // ── Store ──────────────────────────────────────────────────────────────────

  const {
    presentationId,
    selectedProviders,
    costTables,
    selectedCostTableIndex,
    setCostTables,
    updateCostTable,
    setSelectedCostTableIndex,
    goToBuildStep,
  } = useWizardStore();

  // ── Local UI state ─────────────────────────────────────────────────────────

  /** Whether the "Provider prices" collapsible section is open. */
  const [providerPricesOpen, setProviderPricesOpen] = useState(false);

  /** Error message from the AI assist mutation (shown in AiAssistInput). */
  const [assistError, setAssistError] = useState<string | null>(null);

  // ── API queries and mutations ──────────────────────────────────────────────

  const { data: fetchedTables, isLoading } = useCostData(presentationId ?? '');
  const saveCosts = useSaveCosts();
  const costAssist = useCostAssist();

  // ── Initialise store from fetched data ────────────────────────────────────

  /**
   * Seed the store from the API response on first visit.
   */
  useEffect(() => {
    if (isLoading || costTables.length > 0) return;
    if (fetchedTables && fetchedTables.length > 0) {
      setCostTables(fetchedTables);
      setSelectedCostTableIndex(0);
    }
  }, [isLoading, fetchedTables, costTables.length, setCostTables, setSelectedCostTableIndex]);

  // ── Auto-select first table when list is non-empty ─────────────────────────

  useEffect(() => {
    if (costTables.length > 0 && selectedCostTableIndex === null) {
      setSelectedCostTableIndex(0);
    }
  }, [costTables.length, selectedCostTableIndex, setSelectedCostTableIndex]);

  // ── Debounced auto-save ────────────────────────────────────────────────────

  const saveCostsRef = useRef(saveCosts);
  saveCostsRef.current = saveCosts;

  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  /**
   * Schedule an auto-save 500 ms after the last change.
   * Cancels any pending save so rapid edits coalesce into one request.
   *
   * @param tables - The current cost tables to persist.
   */
  const scheduleSave = useCallback(
    (tables: CostTableConfig[]) => {
      if (!presentationId) return;
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
      saveTimerRef.current = setTimeout(() => {
        saveCostsRef.current.mutate(
          { id: presentationId, tables },
          {
            onError: () => {
              toast.error('Could not save costs. Check your connection.', {
                duration: 8_000,
              });
            },
          },
        );
      }, 500);
    },
    [presentationId],
  );

  /** Clean up any pending save timer on unmount. */
  useEffect(() => {
    return () => {
      if (saveTimerRef.current) {
        clearTimeout(saveTimerRef.current);
        // Flush: save immediately on unmount so edits aren't lost.
        const pid = useWizardStore.getState().presentationId;
        const tables = useWizardStore.getState().costTables;
        if (pid && tables.length > 0) {
          saveCostsRef.current.mutate({ id: pid, tables });
        }
      }
    };
  }, []);

  // ── Table mutation handlers ────────────────────────────────────────────────

  /**
   * Update a single table in the store and schedule an auto-save.
   *
   * @param index - Index of the table to update.
   * @param updated - New table value (already recalculated by CostTableEditor).
   */
  const handleTableUpdate = useCallback(
    (index: number, updated: CostTableConfig) => {
      updateCostTable(index, updated);
      const next = costTables.map((t, i) => (i === index ? updated : t));
      scheduleSave(next);
    },
    [costTables, updateCostTable, scheduleSave],
  );

  /**
   * Add a blank cost table, select it, and schedule a save.
   */
  const handleAddTable = useCallback(() => {
    const newTable: CostTableConfig = {
      slideIndex: 0,
      title: `Tabla ${costTables.length + 1}`,
      rows: [],
      total: 0,
    };
    const next = [...costTables, newTable];
    setCostTables(next);
    setSelectedCostTableIndex(next.length - 1);
    scheduleSave(next);
  }, [costTables, setCostTables, setSelectedCostTableIndex, scheduleSave]);

  /**
   * Delete the table at the given index, select the adjacent table, and save.
   *
   * @param index - Index of the table to remove.
   */
  const handleDeleteTable = useCallback(
    (index: number) => {
      const next = costTables.filter((_, i) => i !== index);
      setCostTables(next);
      const nextSelected =
        next.length === 0
          ? null
          : index >= next.length
            ? next.length - 1
            : index;
      setSelectedCostTableIndex(nextSelected);
      scheduleSave(next);
    },
    [costTables, setCostTables, setSelectedCostTableIndex, scheduleSave],
  );

  // ── AI Assist handler ──────────────────────────────────────────────────────

  /**
   * Send an instruction to the cost-adjustment assistant.
   * Updates the store and triggers a save on success.
   *
   * @param instruction - Free-text instruction from the user.
   */
  const handleAssistSubmit = useCallback(
    async (instruction: string) => {
      if (!presentationId) return;
      setAssistError(null);
      try {
        const result = await costAssist.mutateAsync({
          id: presentationId,
          instruction,
          currentTables: costTables,
        });
        setCostTables(result.tables);
        scheduleSave(result.tables);
        toast.success('Costs updated by AI.', { duration: 3_000 });
      } catch (err) {
        const message = extractApiError(err, 'Error processing instruction. Please try again.');
        setAssistError(message);
        throw err;
      }
    },
    [presentationId, costTables, costAssist, setCostTables, scheduleSave],
  );

  // ── Derived values ─────────────────────────────────────────────────────────

  /**
   * Grand total: sum of all table totals across all cost tables.
   */
  const grandTotal = costTables.reduce((sum, t) => sum + t.total, 0);

  /**
   * Average margin percentage across all rows in all tables.
   * Returns 0 when there are no rows.
   */
  const allRows = costTables.flatMap((t) => t.rows);
  const averageMargin =
    allRows.length > 0
      ? allRows.reduce((sum, r) => sum + r.marginPercent, 0) / allRows.length
      : 0;

  /**
   * Provider list for the CostTableEditor dropdown.
   * Derived from selectedProviders in the store.
   */
  const providerOptions = selectedProviders.map((p) => ({
    id: String(p.id),
    name: p.nombre_comercial,
  }));

  /** Currently selected table (or null). */
  const selectedTable =
    selectedCostTableIndex !== null ? (costTables[selectedCostTableIndex] ?? null) : null;

  /**
   * Format a number as currency.
   *
   * @param value - Numeric value.
   * @returns Formatted currency string.
   */
  const formatCurrency = (value: number | undefined | null) =>
    `S/ ${Number(value ?? 0).toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  // ── Render: loading skeleton ───────────────────────────────────────────────

  if (isLoading) {
    return (
      <div className={styles.container} aria-busy aria-label="Loading cost tables...">
        <div className={styles.toolbar}>
          <Skeleton variant="rect" height={32} />
        </div>
        <div className={styles.editorArea}>
          <div className={styles.sidebar}>
            {[1, 2, 3].map((i) => (
              <div key={i} className={styles.sidebarSkeletonItem}>
                <Skeleton variant="rect" height={56} />
              </div>
            ))}
          </div>
          <div className={styles.mainArea}>
            <div className={styles.skeletonMain}>
              <Skeleton variant="rect" height={28} />
              <Skeleton variant="rect" height={200} />
              <p className={styles.loadingMessage}>Computing cost tables...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── Render: main ──────────────────────────────────────────────────────────

  return (
    <div className={styles.container}>
      {/* ── Toolbar ──────────────────────────────────────────────────────── */}
      <div className={styles.toolbar} role="toolbar" aria-label="Costs toolbar">
        {/* Provider chips */}
        {selectedProviders.length > 0 && (
          <div className={styles.providerChips} aria-label="Selected providers">
            {selectedProviders.slice(0, 4).map((p) => (
              <span key={p.id} className={styles.providerChip} title={p.nombre_comercial}>
                {p.nombre_comercial}
              </span>
            ))}
            {selectedProviders.length > 4 && (
              <span className={styles.moreChip}>+{selectedProviders.length - 4} more</span>
            )}
          </div>
        )}

        <div className={styles.toolbarDivider} aria-hidden />

        {/* Add table */}
        <button
          type="button"
          className={styles.addTableButton}
          onClick={handleAddTable}
          aria-label="Add new cost table"
        >
          + Add table
        </button>

        <div className={styles.toolbarSpacer} />

        {/* Summary metrics */}
        <div className={styles.metric} aria-label={`Grand total: ${formatCurrency(grandTotal)}`}>
          <span className={styles.metricLabel}>Grand total</span>
          <span className={styles.metricValue}>{formatCurrency(grandTotal)}</span>
        </div>

        <div className={styles.toolbarDivider} aria-hidden />

        <div
          className={styles.metric}
          aria-label={`Average margin: ${averageMargin.toFixed(1)}%`}
        >
          <span className={styles.metricLabel}>Avg. margin</span>
          <span className={styles.metricValue}>{averageMargin.toFixed(1)}%</span>
        </div>
      </div>

      {/* ── Editor area ──────────────────────────────────────────────────── */}
      <div className={styles.editorArea}>
        {/* ── Left sidebar ─────────────────────────────────────────────── */}
        <aside className={styles.sidebar} aria-label="Cost tables list">
          <div className={styles.sidebarHeader}>
            <h2 className={styles.sidebarHeaderTitle}>Tables</h2>
          </div>

          <div className={styles.sidebarList}>
            {costTables.length === 0 ? (
              <p className={styles.sidebarEmpty}>No tables yet.</p>
            ) : (
              costTables.map((table, idx) => (
                <button
                  key={idx}
                  type="button"
                  className={`${styles.tableCard} ${selectedCostTableIndex === idx ? styles.tableCardSelected : ''}`}
                  onClick={() => setSelectedCostTableIndex(idx)}
                  aria-pressed={selectedCostTableIndex === idx}
                  aria-label={`Table: ${table.title || `Table ${idx + 1}`}`}
                >
                  <span className={styles.tableCardTitle}>
                    {table.title || `Table ${idx + 1}`}
                  </span>
                  <span className={styles.tableCardMeta}>
                    {table.rows.length} {table.rows.length === 1 ? 'row' : 'rows'}
                  </span>
                  <span className={styles.tableCardTotal}>{formatCurrency(table.total)}</span>
                </button>
              ))
            )}
          </div>

          <div className={styles.sidebarFooter}>
            <button
              type="button"
              className={styles.sidebarAddButton}
              onClick={handleAddTable}
              aria-label="Add new cost table"
            >
              + Add table
            </button>
          </div>
        </aside>

        {/* ── Main area ────────────────────────────────────────────────── */}
        <div className={styles.mainArea} role="main" aria-label="Cost table editor">
          {selectedTable === null ? (
            <div className={styles.emptyMain}>
              <p className={styles.emptyMainTitle}>No cost tables</p>
              <p className={styles.emptyMainBody}>
                Click "+ Add table" to create the first table.
              </p>
              <button
                type="button"
                className={styles.emptyMainButton}
                onClick={handleAddTable}
                aria-label="Add first cost table"
              >
                + Add table
              </button>
            </div>
          ) : (
            <div className={styles.mainContent}>
              {/* Cost table editor */}
              <CostTableEditor
                table={selectedTable}
                onUpdate={(updated) => {
                  if (selectedCostTableIndex !== null)
                    handleTableUpdate(selectedCostTableIndex, updated);
                }}
                providers={providerOptions}
                onDelete={() => {
                  if (selectedCostTableIndex !== null)
                    handleDeleteTable(selectedCostTableIndex);
                }}
              />

              {/* Provider prices reference (collapsible) */}
              {selectedProviders.length > 0 && (
                <div className={styles.providerPricesSection}>
                  <button
                    type="button"
                    className={styles.providerPricesToggle}
                    onClick={() => setProviderPricesOpen((v) => !v)}
                    aria-expanded={providerPricesOpen}
                    aria-controls="provider-prices-content"
                  >
                    <span>Provider reference prices</span>
                    <span
                      className={`${styles.toggleChevron} ${providerPricesOpen ? styles.toggleChevronOpen : ''}`}
                      aria-hidden
                    >
                      ▾
                    </span>
                  </button>

                  {providerPricesOpen && (
                    <div
                      id="provider-prices-content"
                      className={styles.providerPricesContent}
                      role="region"
                      aria-label="Provider reference prices"
                    >
                      {selectedProviders.map((provider) => (
                        <div key={provider.id} className={styles.providerPriceCard}>
                          <span className={styles.providerPriceCardName}>
                            {provider.nombre_comercial}
                          </span>
                          <span className={styles.providerPriceCardNote}>
                            Check the quotation for specific prices.
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* AI Assist */}
              <div className={styles.assistSection}>
                <div className={styles.assistHeader}>
                  <div className={styles.assistDot} aria-hidden />
                  <span className={styles.assistLabel}>AI Assistant</span>
                </div>
                <AiAssistInput
                  placeholder="Ask AI to modify the costs..."
                  onSubmit={handleAssistSubmit}
                  isLoading={costAssist.isPending}
                  error={assistError}
                  onClearError={() => setAssistError(null)}
                />
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ── Footer row ───────────────────────────────────────────────────── */}
      <div className={styles.footerRow}>
        <Button
          variant="primary"
          size="md"
          onClick={() => goToBuildStep(6)}
          aria-label="Continue to generation step"
        >
          Continue to generate &#8594;
        </Button>
      </div>
    </div>
  );
};
